import portals from "../../data/portals.json";
import { Swiper, SwiperSlide } from "swiper/react";
import { useEffect, useRef, useState } from "react";
import { useScreenDetector } from "../../hooks/useScreenDetector";

import { awsUrl, triggerGAEvent } from "../../helpers";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules";
import Video from "../../common/PortalVideo";
import { useUserContext } from "../../context/UserContext";

const Details = ({ portalKey, key, delay }) => {
  const { isMobile, isDesktop } = useScreenDetector();
  const swiperLockedRef = useRef();
  const swiperUnlockedRef = useRef();
  const [delayTime, setDelayTime] = useState(delay);
  const { allVideosPlaying, setAllVideosPlaying } = useUserContext();

  const pagination = {
    el: ".custom-pagination",
    clickable: true,
    type: "bullets",
    bulletClass: "swiper-custom-bullet",
    bulletActiveClass: "swiper-custom-bullet-active",
    renderBullet: function (index, className) {
      return `<span role="button" class="swiper-custom-bullet z-[9999]"></span>`;
    }
  };

  const handleVideo = () => {
    if (allVideosPlaying) {
      setAllVideosPlaying(false);
    } else {
      setAllVideosPlaying(true);
    }
  };

  useEffect(() => {
    setDelayTime(delay);
  }, [delayTime, setDelayTime, delay]);

  const handlePrev = (state) => {
    let swiperRef = state === "locked" ? swiperLockedRef.current : swiperUnlockedRef.current;

    setDelayTime(delay);

    triggerGAEvent("portal_" + portalKey + "_next-slide");

    if (!swiperRef) return;
    swiperRef.swiper.slidePrev();
  };

  const handleNext = (state) => {
    let swiperRef = state === "locked" ? swiperLockedRef.current : swiperUnlockedRef.current;
    setDelayTime(delay);

    triggerGAEvent("portal_" + portalKey + "_next-slide");

    if (!swiperRef) return;
    if (swiperRef.swiper.isEnd) {
      swiperRef.swiper.slideTo(0);
    } else {
      swiperRef.swiper.slideNext();
    }
  };

  return (
    <div>
      <div className="absolute right-0 top-12 md:right-0 lg:top-12 z-[10] w-[52px] lg:w-[50px] h-[50px] pointer-events-auto bg-dark-blue">
        <span aria-hidden className="absolute sr-only mt-2 top-0">
          {allVideosPlaying ? "Pause" : "Play"} all videos
        </span>
        <div
          tabIndex={0}
          role="button"
          onKeyDown={() => handleVideo()}
          onClick={() => handleVideo()}
          aria-label={(allVideosPlaying ? "Pause" : "Play") + " all videos"}
          className={"absolute top-0 left-2 lg:left-0 block"}
        >
          {!allVideosPlaying && (
            <span
              aria-hidden
              className="material-symbols-rounded filled opacity-100 hover:opacity-75 text-4xl lg:text-5xl text-white"
            >
              play_arrow
            </span>
          )}
          {allVideosPlaying && (
            <span className="material-symbols-rounded filled opacity-100 hover:opacity-75 text-4xl lg:text-5xl text-white">
              pause
            </span>
          )}
        </div>
      </div>
      {portals.map((portal, idx) => {
        if (portal.key === portalKey) {
          if (new Date(portal.release_timestamp) > Date.now()) {
            return (
              <div key={idx} className="text-white">
                <div className="absolute bottom-[14%] left-1/2 -translate-x-1/2 z-20  w-full px-3">
                  <div className="flex items-baseline justify-center">
                    {" "}
                    <img
                      aria-hidden="true"
                      alt="map key"
                      className="hidden lg:inline w-[63px] mb-6 mx-4"
                      src={awsUrl + `/media/map-key.png?v=1`}
                      srcSet={awsUrl + `/media/map-key.png?v=1`}
                    />{" "}
                    <h4 className="inline text-tempo text-[36px] leading-[40px] lg:text-[55px] lg:leading-[60px] lg:mt-4 font-black text-shadow uppercase relative z-10">
                      {portal.name} portal
                    </h4>
                  </div>

                  <p className="max-w-[750px] mx-auto text-[19px] leading-[22px] px-3 mt-0 lg:-mt-2 mb-6 ">
                    {portal.locked.copy}
                  </p>

                  <div className="flex flex-row justify-center z-20 pointer-events-auto">
                    <div
                      tabIndex={0}
                      role="button"
                      className="prev-arrow cursor-pointer hover:opacity-75"
                      onClick={() => handlePrev("locked")}
                      aria-label="Go to previous slide"
                    >
                      {" "}
                      <img
                        src={awsUrl + `/media/double-arrow.svg`}
                        alt="left arrow"
                        className="w-[18px] mr-3"
                        aria-hidden="true"
                      />
                    </div>

                    <div className="custom-pagination"></div>
                    <div
                      role="button"
                      tabIndex={0}
                      className="next-arrow cursor-pointer hover:opacity-75"
                      onClick={() => handleNext("locked")}
                      aria-label="Go to next slide"
                    >
                      {" "}
                      <img
                        src={awsUrl + `/media/double-arrow.svg`}
                        alt="right arrow"
                        className="w-[18px] ml-3 rotate-180"
                        aria-hidden="true"
                      />
                    </div>
                  </div>

                  <p className="font-semibold relative z-10 mx-auto text-[19px] leading-[22px] mt-6">
                    Come back in {portal.release_date} to unlock all the details
                  </p>
                </div>

                <div className="slider-container h-[75vh] lg:h-full w-full absolute top-0 left-0 text-light-blue">
                  <Swiper
                    effect={"fade"}
                    ref={swiperLockedRef}
                    loop="true"
                    spaceBetween={50}
                    slidesPerView={1}
                    pagination={pagination}
                    modules={[Autoplay, EffectFade, Pagination, Navigation]}
                    className="h-full"
                    autoplay={{
                      delay: delayTime,
                      disableOnInteraction: false
                    }}
                    onSlideChange={(swiper) =>
                      triggerGAEvent(
                        "home_portal_" + swiper.slides[swiper.activeIndex].dataset.title
                      )
                    }
                  >
                    {portal.locked.assets.map((asset, idx) => {
                      if (asset.type === "mp4") {
                        return (
                          <SwiperSlide key={idx} data-title={portal.key + `_` + asset.media}>
                            <Video
                              autoplay={allVideosPlaying}
                              videoplaying={"true"}
                              name={`portals/` + portal.key + `/` + asset.media}
                            />{" "}
                          </SwiperSlide>
                        );
                      } else {
                        return (
                          <SwiperSlide key={idx} data-title={asset.media}>
                            {isMobile && (
                              <img
                                src={
                                  awsUrl +
                                  `/media/portals/` +
                                  portal.key +
                                  `/` +
                                  asset.media +
                                  `-m.jpg`
                                }
                                alt={asset.media}
                              />
                            )}
                            {isDesktop && (
                              <img
                                className="w-full"
                                src={
                                  awsUrl +
                                  `/media/portals/` +
                                  portal.key +
                                  `/` +
                                  asset.media +
                                  `.jpg`
                                }
                                alt={asset.media}
                              />
                            )}
                          </SwiperSlide>
                        );
                      }
                    })}
                  </Swiper>
                </div>
              </div>
            );
          } else {
            return (
              <div className="text-white">
                <div className="absolute bottom-[14%] left-1/2 -translate-x-1/2 z-20 w-full">
                  <div className="flex items-baseline justify-center">
                    {" "}
                    <img
                      alt="icon of map key"
                      className="hidden lg:inline w-[63px] mb-6 mx-4"
                      src={awsUrl + `/media/map-key.png?v=1`}
                      srcSet={awsUrl + `/media/map-key.png?v=1`}
                      aria-hidden="true"
                    />{" "}
                    <h4 className="inline text-tempo text-[36px] leading-[40px] lg:text-[55px] lg:leading-[60px] lg:mt-4 font-black text-shadow uppercase relative z-10">
                      {portal.name} portal
                    </h4>
                  </div>

                  <p className="max-w-[801px] mx-auto text-[19px] leading-[22px] px-3 mt-0 lg:-mt-2 mb-6">
                    {portal.unlocked.copy}
                  </p>

                  <div className="flex flex-row justify-center relative pointer-events-auto">
                    {" "}
                    <div
                      className="prev-arrow cursor-pointer hover:opacity-75"
                      onClick={() => handlePrev("unlocked")}
                      aria-label="Go to previous slide"
                    >
                      {" "}
                      <img
                        src={awsUrl + `/media/double-arrow.svg`}
                        alt="left arrow"
                        className="w-[18px] mr-3"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="custom-pagination"></div>
                    <div
                      className="next-arrow cursor-pointer hover:opacity-75"
                      onClick={() => handleNext("unlocked")}
                      aria-label="Go to next slide"
                    >
                      {" "}
                      <img
                        src={awsUrl + `/media/double-arrow.svg`}
                        alt="right arrow"
                        aria-hidden="true"
                        className="w-[18px] ml-3 rotate-180"
                      />
                    </div>
                  </div>

                  <p className="font-semibold relative z-10 mt-6 mx-auto text-[19px] leading-[22px]">
                    Come back in {portal.release_date} to unlock all the details
                  </p>
                </div>

                <div className="slider-container h-[70vh] lg:h-full w-full absolute top-0 left-0 text-light-blue">
                  <Swiper
                    ref={swiperUnlockedRef}
                    effect={"fade"}
                    loop={"true"}
                    spaceBetween={50}
                    slidesPerView={1}
                    pagination={pagination}
                    modules={[Autoplay, EffectFade, Pagination]}
                    className="h-full"
                    autoplay={{
                      delay: delayTime,
                      disableOnInteraction: false
                    }}
                    onSlideChange={(swiper) =>
                      triggerGAEvent(
                        "home_portal_" + swiper.slides[swiper.activeIndex].dataset.title
                      )
                    }
                  >
                    {portal.unlocked.assets.map((asset, idx) => {
                      if (asset.type === "mp4") {
                        return (
                          <SwiperSlide key={idx} data-title={asset.media}>
                            <Video
                              autoplay={allVideosPlaying}
                              videoplaying={"true"}
                              name={`portals/` + portal.key + `/` + asset.media}
                            />{" "}
                          </SwiperSlide>
                        );
                      } else {
                        return (
                          <SwiperSlide key={idx} data-title={asset.media}>
                            {isMobile && (
                              <img
                                alt={asset.media}
                                src={
                                  awsUrl +
                                  `/media/portals/` +
                                  portal.key +
                                  `/` +
                                  asset.media +
                                  `-m.jpg`
                                }
                              />
                            )}
                            {isDesktop && (
                              <img
                                alt={asset.media}
                                className="w-full"
                                src={
                                  awsUrl +
                                  `/media/portals/` +
                                  portal.key +
                                  `/` +
                                  asset.media +
                                  `.jpg`
                                }
                              />
                            )}
                          </SwiperSlide>
                        );
                      }
                    })}
                  </Swiper>
                </div>
              </div>
            );
          }
        } else {
          return null;
        }
      })}
    </div>
  );
};

export default Details;
